import { MessageCircle, Clock, Globe, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Consultation() {
  const whatsappNumber = '91 82950 80493';
  const consultMessage = 'Hello! I would like to know more about online consultation.';
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(consultMessage)}`;

  return (
    <div className="min-h-screen py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-[#2D3436] mb-4" data-testid="consultation-page-title">
            Online <span className="text-primary">Consultation</span>
          </h1>
          <p className="text-lg text-[#636E72] max-w-3xl mx-auto">
            Get expert Ayurvedic consultation from anywhere in the world. We offer personalized treatment plans for national and international patients.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white p-8 rounded-xl border border-border/50 shadow-sm">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <Globe className="text-primary" size={32} />
            </div>
            <h3 className="text-2xl font-heading font-semibold text-[#2D3436] mb-4">National & International</h3>
            <p className="text-[#636E72] leading-relaxed">
              Our online consultation services are available for patients across India and internationally. Connect with certified Ayurvedic doctors from the comfort of your home.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl border border-border/50 shadow-sm">
            <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6">
              <Clock className="text-secondary" size={32} />
            </div>
            <h3 className="text-2xl font-heading font-semibold text-[#2D3436] mb-4">Flexible Timing</h3>
            <p className="text-[#636E72] leading-relaxed">
              Consultation timing and process will be shared directly by the doctor after you connect via WhatsApp. We work around your schedule.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-12 rounded-2xl mb-16">
          <h2 className="text-3xl font-heading font-semibold text-[#2D3436] mb-8 text-center">How It Works</h2>
          
          <div className="space-y-6 max-w-3xl mx-auto">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                1
              </div>
              <div>
                <h4 className="font-semibold text-[#2D3436] mb-2">Contact via WhatsApp</h4>
                <p className="text-[#636E72]">
                  Click the button below to send a WhatsApp message expressing your interest in consultation.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                2
              </div>
              <div>
                <h4 className="font-semibold text-[#2D3436] mb-2">Doctor Connects</h4>
                <p className="text-[#636E72]">
                  Our doctor will respond and share available consultation slots and the process.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                3
              </div>
              <div>
                <h4 className="font-semibold text-[#2D3436] mb-2">Consultation Session</h4>
                <p className="text-[#636E72]">
                  Detailed discussion about your health concerns, lifestyle, and personalized treatment plan.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                4
              </div>
              <div>
                <h4 className="font-semibold text-[#2D3436] mb-2">Follow-up Support</h4>
                <p className="text-[#636E72]">
                  Ongoing support and guidance throughout your treatment journey via WhatsApp.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-12 rounded-2xl border border-border/50 shadow-sm">
          <h2 className="text-3xl font-heading font-semibold text-[#2D3436] mb-6 text-center">What to Expect</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
              <span className="text-[#636E72]">Comprehensive health assessment</span>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
              <span className="text-[#636E72]">Personalized treatment plan</span>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
              <span className="text-[#636E72]">Diet and lifestyle recommendations</span>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
              <span className="text-[#636E72]">Ayurvedic medicine prescription</span>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
              <span className="text-[#636E72]">Regular follow-ups</span>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
              <span className="text-[#636E72]">Continuous support via WhatsApp</span>
            </div>
          </div>

          <div className="text-center">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#20B558] text-white rounded-full px-8 py-6 text-lg" data-testid="start-consultation-button">
                <MessageCircle size={20} className="mr-2" />
                Start Consultation on WhatsApp
              </Button>
            </a>
            <p className="text-sm text-[#636E72] mt-4">
              No payment gateway required - consultation fees will be discussed directly
            </p>
          </div>
        </div>

        <div className="mt-16 bg-accent/30 p-8 rounded-xl text-center">
          <h3 className="text-2xl font-heading font-semibold text-[#2D3436] mb-4">Special Offers</h3>
          <p className="text-lg text-[#636E72] mb-2">
            <strong>Free Consultation</strong> for Police Officers and Army Personnel
          </p>
          <p className="text-sm text-[#636E72]">
            Mention your service when contacting us via WhatsApp
          </p>
        </div>
      </div>
    </div>
  );
}